import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { getUserJourney } from '../../services/api';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const ACTION_LABELS = {
  auth_login: 'Login',
  auth_signup: 'Signup',
  screener_run: 'Run Screener',
  screener_save_params: 'Save Params',
  screener_delete_params: 'Delete Params',
  watchlist_run_simulation: 'Run Simulation',
  watchlist_simulate_group: 'Simulate Group',
  watchlist_run_bulk: 'Bulk Simulation',
  watchlist_refresh_contract: 'Refresh Contract',
  watchlist_refresh_group: 'Refresh Group',
  watchlist_create_group: 'Create Group',
  watchlist_update_group: 'Update Group',
  watchlist_delete_group: 'Delete Group',
  watchlist_assign_contracts: 'Assign Contracts',
  watchlist_remove_contract: 'Remove Contract',
  watchlist_create_contract: 'Create Contract',
  watchlist_delete_contract: 'Delete Contract',
  watchlist_reset_countdown: 'Reset Countdown',
  watchlist_update_target: 'Update Target',
  feedback_submit: 'Submit Feedback',
};

const TOOL_COLORS = {
  login: '#f59e0b',
  auth: '#f59e0b',
  screener: '#10b981',
  watchlist: '#3b82f6',
  dashboard_builder: '#8b5cf6',
  feedback: '#6b7280',
};

const TOOL_LABELS = {
  login: 'Login',
  auth: 'Auth',
  screener: 'Screener',
  watchlist: 'Watchlist',
  dashboard_builder: 'Dashboard Builder',
  feedback: 'Feedback',
};

const TIER_COLORS = {
  free: { bg: '#374151', color: '#9ca3af' },
  essential: { bg: '#065f46', color: '#d1fae5' },
  plus: { bg: '#1e40af', color: '#dbeafe' },
  premium: { bg: '#5b21b6', color: '#ede9fe' },
  admin: { bg: '#991b1b', color: '#fecaca' },
};

function formatTimestamp(ts) {
  if (!ts) return '--';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatSessionDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function formatEventTime(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

function getSessionDuration(events) {
  if (!events || events.length < 2) return null;
  const first = new Date(events[0].timestamp);
  const last = new Date(events[events.length - 1].timestamp);
  const diffMs = last - first;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 1) return '< 1 min';
  if (diffMin < 60) return `${diffMin} min`;
  const hours = Math.floor(diffMin / 60);
  const mins = diffMin % 60;
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function getGapBetweenSessions(prevSession, nextSession) {
  if (!prevSession || !nextSession) return null;
  const prevLast = new Date(prevSession[prevSession.length - 1].timestamp);
  const nextFirst = new Date(nextSession[0].timestamp);
  const diffMs = nextFirst - prevLast;
  const diffMin = Math.round(diffMs / 60000);
  if (diffMin < 60) return `${diffMin} min gap`;
  const hours = Math.floor(diffMin / 60);
  if (hours < 24) return `${hours}h gap`;
  const days = Math.floor(hours / 24);
  return `${days}d gap`;
}

function formatMetadata(metadata) {
  if (!metadata) return null;
  const parts = [];
  for (const [key, value] of Object.entries(metadata)) {
    if (key === 'scenarios') parts.push(`${value} scenarios`);
    else if (key === 'group_name') parts.push(`group: ${value}`);
    else if (key === 'ticker') parts.push(`ticker: ${value}`);
    else if (key === 'tickers') parts.push(`tickers: ${Array.isArray(value) ? value.join(', ') : value}`);
    else if (key === 'count') parts.push(`${value} items`);
    else parts.push(`${key}: ${value}`);
  }
  return parts.join(' | ');
}

function ReportsUserJourney({ initialUsername = '' }) {
  const [username, setUsername] = useState(initialUsername);
  const [days, setDays] = useState(30);
  const [journeyData, setJourneyData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);
  const prevInitialUsername = useRef(initialUsername);

  // Auto-search when navigated from User Intelligence tab
  useEffect(() => {
    if (initialUsername && initialUsername !== prevInitialUsername.current) {
      prevInitialUsername.current = initialUsername;
      setUsername(initialUsername);
      // Trigger search after state update
      const doSearch = async () => {
        try {
          setLoading(true);
          setError(null);
          setSearched(true);
          const result = await getUserJourney(initialUsername.trim(), days);
          setJourneyData(result);
        } catch (err) {
          if (err.response?.status === 404) {
            setError(`User "${initialUsername}" not found`);
          } else {
            setError(err.message || 'Failed to fetch user journey');
          }
          setJourneyData(null);
        } finally {
          setLoading(false);
        }
      };
      doSearch();
    }
  }, [initialUsername, days]);

  const handleSearch = useCallback(async () => {
    if (!username.trim()) {
      setError('Please enter a username');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSearched(true);
      const result = await getUserJourney(username.trim(), days);
      setJourneyData(result);
    } catch (err) {
      if (err.response?.status === 404) {
        setError(`User "${username}" not found`);
        setJourneyData(null);
      } else {
        setError(err.message || 'Failed to fetch user journey');
        setJourneyData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [username, days]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Aggregate events by tool for pie chart
  const toolDistribution = useMemo(() => {
    if (!journeyData?.sessions) return [];
    const counts = {};
    journeyData.sessions.forEach((session) => {
      session.forEach((event) => {
        const tool = event.tool || 'unknown';
        counts[tool] = (counts[tool] || 0) + 1;
      });
    });
    const total = Object.values(counts).reduce((sum, c) => sum + c, 0);
    return Object.entries(counts)
      .map(([tool, count]) => ({
        name: TOOL_LABELS[tool] || tool,
        tool,
        value: count,
        percentage: total > 0 ? ((count / total) * 100).toFixed(1) : '0',
      }))
      .sort((a, b) => b.value - a.value);
  }, [journeyData]);

  // Aggregate events by date for bar chart
  const dailyActivity = useMemo(() => {
    if (!journeyData?.sessions) return [];
    const dateCounts = {};
    journeyData.sessions.forEach((session) => {
      session.forEach((event) => {
        if (!event.timestamp) return;
        const dateStr = event.timestamp.split('T')[0];
        dateCounts[dateStr] = (dateCounts[dateStr] || 0) + 1;
      });
    });
    return Object.entries(dateCounts)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => {
        const parts = date.split('-');
        return {
          date,
          dateLabel: `${parts[1]}/${parts[2]}`,
          count,
        };
      });
  }, [journeyData]);

  // Custom tooltip for pie chart
  const PieTooltip = ({ active, payload }) => {
    if (!active || !payload || payload.length === 0) return null;
    const data = payload[0].payload;
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '6px',
        padding: '10px 14px',
        color: '#fff',
        fontSize: '13px',
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: TOOL_COLORS[data.tool] || '#6b7280' }}>
          {data.name}
        </p>
        <p style={{ margin: 0, color: '#ccc' }}>
          {data.value} events ({data.percentage}%)
        </p>
      </div>
    );
  };

  // Custom tooltip for bar chart
  const BarTooltip = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;
    return (
      <div style={{
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '6px',
        padding: '10px 14px',
        color: '#fff',
        fontSize: '13px',
      }}>
        <p style={{ margin: '0 0 4px 0', fontWeight: 600, color: '#ccc' }}>
          {label}
        </p>
        <p style={{ margin: 0, color: '#218aff' }}>
          Events: {payload[0].value}
        </p>
      </div>
    );
  };

  const user = journeyData?.user;
  const sessions = journeyData?.sessions || [];
  const tierKey = (user?.tier || 'free').toLowerCase();
  const tierStyle = TIER_COLORS[tierKey] || TIER_COLORS.free;

  return (
    <div className="analytics-user-journey" style={{ padding: '10px' }}>
      <div className="reports-section-header">
        <h3>User Journey</h3>
      </div>

      {/* Search Form */}
      <div
        className="analytics-user-search"
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: '15px',
          marginBottom: '30px',
          flexWrap: 'wrap',
          background: '#212121',
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #333',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: 1, minWidth: '180px' }}>
          <label style={{ fontSize: '14px', color: '#aeb9cc', fontWeight: 500 }}>Username:</label>
          <input
            type="text"
            className="search-input"
            placeholder="Enter username..."
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyPress={handleKeyPress}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', minWidth: '140px' }}>
          <label style={{ fontSize: '14px', color: '#aeb9cc', fontWeight: 500 }}>Days:</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="period-select"
          >
            <option value={7}>Last 7 Days</option>
            <option value={14}>Last 14 Days</option>
            <option value={30}>Last 30 Days</option>
            <option value={60}>Last 60 Days</option>
            <option value={90}>Last 90 Days</option>
          </select>
        </div>
        <button
          className="btn-primary"
          onClick={handleSearch}
          disabled={loading}
          style={{ minWidth: '100px' }}
        >
          {loading ? 'Searching...' : 'Search'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="reports-error">
          <p>{error}</p>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="roles-loading">
          <div className="spinner"></div>
          <p>Loading user journey...</p>
        </div>
      )}

      {/* Results */}
      {!loading && journeyData && user && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>

          {/* User Profile Card */}
          <div
            className="analytics-user-profile-card"
            style={{
              background: '#212121',
              borderRadius: '10px',
              padding: '24px',
              border: '1px solid #333',
              borderLeft: '4px solid #218aff',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '20px' }}>
              <div>
                <h4 style={{ margin: '0 0 6px 0', color: '#fff', fontSize: '22px', fontWeight: 700 }}>
                  {user.username}
                </h4>
                <p style={{ margin: '0 0 12px 0', color: '#aeb9cc', fontSize: '14px' }}>
                  {user.email || 'No email'}
                </p>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <span
                    style={{
                      display: 'inline-block',
                      padding: '4px 14px',
                      borderRadius: '20px',
                      fontSize: '12px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      backgroundColor: tierStyle.bg,
                      color: tierStyle.color,
                    }}
                  >
                    {user.tier || 'Free'}
                  </span>
                  {user.stripe_status && (
                    <span style={{
                      fontSize: '12px',
                      color: user.stripe_status === 'active' ? '#10b981' : '#f59e0b',
                      fontWeight: 500,
                    }}>
                      Stripe: {user.stripe_status}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#218aff', lineHeight: 1 }}>
                    {journeyData.total_sessions || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#aeb9cc', marginTop: '4px' }}>Sessions</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '28px', fontWeight: 700, color: '#10b981', lineHeight: 1 }}>
                    {journeyData.total_events || 0}
                  </div>
                  <div style={{ fontSize: '12px', color: '#aeb9cc', marginTop: '4px' }}>Events</div>
                </div>
              </div>
            </div>
            <div style={{
              display: 'flex',
              gap: '30px',
              marginTop: '16px',
              paddingTop: '14px',
              borderTop: '1px solid #333',
              flexWrap: 'wrap',
            }}>
              <div style={{ fontSize: '13px' }}>
                <span style={{ color: '#888' }}>Joined: </span>
                <span style={{ color: '#ccc' }}>{formatTimestamp(user.date_joined)}</span>
              </div>
              <div style={{ fontSize: '13px' }}>
                <span style={{ color: '#888' }}>Last Login: </span>
                <span style={{ color: '#ccc' }}>{formatTimestamp(user.last_login)}</span>
              </div>
            </div>
          </div>

          {/* Charts Row */}
          <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '24px' }}>

            {/* Activity Distribution Pie */}
            <div
              className="analytics-chart-container"
              style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '10px',
                padding: '20px',
                margin: 0,
              }}
            >
              <h4 style={{ color: '#fff', margin: '0 0 12px 0', fontSize: '15px' }}>
                Activity by Tool
              </h4>
              {toolDistribution.length === 0 ? (
                <p className="no-data">No activity data.</p>
              ) : (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={toolDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={90}
                        paddingAngle={3}
                        dataKey="value"
                        stroke="none"
                      >
                        {toolDistribution.map((entry) => (
                          <Cell
                            key={entry.tool}
                            fill={TOOL_COLORS[entry.tool] || '#6b7280'}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '8px' }}>
                    {toolDistribution.map((entry) => (
                      <div key={entry.tool} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <span style={{
                          width: '10px',
                          height: '10px',
                          borderRadius: '2px',
                          backgroundColor: TOOL_COLORS[entry.tool] || '#6b7280',
                          flexShrink: 0,
                        }} />
                        <span style={{ color: '#ccc', flex: 1 }}>{entry.name}</span>
                        <span style={{ color: '#888' }}>{entry.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Daily Activity Bar */}
            <div
              className="analytics-chart-container"
              style={{
                backgroundColor: '#1a1a1a',
                border: '1px solid #333',
                borderRadius: '10px',
                padding: '20px',
                margin: 0,
              }}
            >
              <h4 style={{ color: '#fff', margin: '0 0 12px 0', fontSize: '15px' }}>
                Daily Activity
              </h4>
              {dailyActivity.length === 0 ? (
                <p className="no-data">No daily activity data.</p>
              ) : (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={dailyActivity}>
                    <CartesianGrid stroke="#333" strokeDasharray="3 3" />
                    <XAxis
                      dataKey="dateLabel"
                      tick={{ fill: '#999', fontSize: 12 }}
                      tickLine={{ stroke: '#555' }}
                      axisLine={{ stroke: '#555' }}
                    />
                    <YAxis
                      tick={{ fill: '#999', fontSize: 12 }}
                      tickLine={{ stroke: '#555' }}
                      axisLine={{ stroke: '#555' }}
                      allowDecimals={false}
                    />
                    <Tooltip content={<BarTooltip />} />
                    <Bar
                      dataKey="count"
                      name="Events"
                      fill="#218aff"
                      radius={[3, 3, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          {/* Session Timeline */}
          <div
            className="analytics-session-timeline"
            style={{
              background: '#212121',
              borderRadius: '10px',
              padding: '24px',
              border: '1px solid #333',
            }}
          >
            <h4 style={{ color: '#fff', margin: '0 0 20px 0', fontSize: '18px' }}>
              Session Timeline
              <span style={{ fontSize: '13px', color: '#888', fontWeight: 400, marginLeft: '12px' }}>
                {sessions.length} session{sessions.length !== 1 ? 's' : ''} in last {journeyData.days || days} days
              </span>
            </h4>

            {sessions.length === 0 ? (
              <p className="no-data">No sessions found for this period.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                {sessions.map((session, sessionIdx) => {
                  const sessionDuration = getSessionDuration(session);
                  const firstEvent = session[0];

                  return (
                    <React.Fragment key={sessionIdx}>
                      {/* Gap indicator between sessions */}
                      {sessionIdx > 0 && (
                        <div
                          className="analytics-session-gap"
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px 0 12px 18px',
                          }}
                        >
                          <div style={{
                            width: '2px',
                            height: '24px',
                            background: 'repeating-linear-gradient(to bottom, #555 0px, #555 4px, transparent 4px, transparent 8px)',
                            marginLeft: '3px',
                          }} />
                          <span style={{
                            fontSize: '11px',
                            color: '#666',
                            fontWeight: 500,
                            background: '#2a2a2a',
                            padding: '3px 10px',
                            borderRadius: '10px',
                            border: '1px solid #3a3a3a',
                            letterSpacing: '0.3px',
                          }}>
                            {getGapBetweenSessions(sessions[sessionIdx - 1], session)}
                          </span>
                        </div>
                      )}

                      {/* Session block */}
                      <div
                        className="analytics-session-block"
                        style={{
                          background: '#1a1a1a',
                          borderRadius: '8px',
                          border: '1px solid #333',
                          overflow: 'hidden',
                        }}
                      >
                        {/* Session header */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '12px 18px',
                          background: '#1f1f1f',
                          borderBottom: '1px solid #333',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <span style={{
                              fontSize: '11px',
                              fontWeight: 700,
                              color: '#218aff',
                              background: 'rgba(33, 138, 255, 0.12)',
                              padding: '3px 8px',
                              borderRadius: '4px',
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}>
                              Session {sessionIdx + 1}
                            </span>
                            <span style={{ fontSize: '13px', color: '#ccc', fontWeight: 500 }}>
                              {formatSessionDate(firstEvent?.timestamp)}
                            </span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                            {sessionDuration && (
                              <span style={{ fontSize: '12px', color: '#888' }}>
                                {sessionDuration}
                              </span>
                            )}
                            <span style={{
                              fontSize: '11px',
                              color: '#666',
                              background: '#2a2a2a',
                              padding: '2px 8px',
                              borderRadius: '3px',
                            }}>
                              {session.length} event{session.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                        </div>

                        {/* Events list with timeline */}
                        <div style={{ padding: '8px 0' }}>
                          {session.map((event, eventIdx) => {
                            const toolColor = TOOL_COLORS[event.tool] || '#6b7280';
                            const actionLabel = ACTION_LABELS[event.action] || event.action;
                            const metaStr = formatMetadata(event.metadata);
                            const isLast = eventIdx === session.length - 1;

                            return (
                              <div
                                key={eventIdx}
                                className="analytics-event-item"
                                style={{
                                  display: 'flex',
                                  alignItems: 'flex-start',
                                  padding: '8px 18px',
                                  position: 'relative',
                                  transition: 'background 0.15s',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.background = '#222'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                              >
                                {/* Timeline column */}
                                <div style={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  width: '20px',
                                  marginRight: '14px',
                                  flexShrink: 0,
                                  position: 'relative',
                                }}>
                                  {/* Dot */}
                                  <div
                                    className="analytics-event-dot"
                                    style={{
                                      width: '10px',
                                      height: '10px',
                                      borderRadius: '50%',
                                      backgroundColor: toolColor,
                                      flexShrink: 0,
                                      marginTop: '4px',
                                      position: 'relative',
                                      zIndex: 1,
                                      boxShadow: `0 0 6px ${toolColor}40`,
                                    }}
                                  />
                                  {/* Connecting line */}
                                  {!isLast && (
                                    <div style={{
                                      width: '2px',
                                      flexGrow: 1,
                                      minHeight: '20px',
                                      backgroundColor: '#333',
                                      marginTop: '4px',
                                    }} />
                                  )}
                                </div>

                                {/* Time */}
                                <div style={{
                                  width: '72px',
                                  flexShrink: 0,
                                  fontSize: '12px',
                                  color: '#888',
                                  fontWeight: 500,
                                  fontFamily: 'monospace',
                                  paddingTop: '2px',
                                }}>
                                  {formatEventTime(event.timestamp)}
                                </div>

                                {/* Action + metadata */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <span style={{
                                      fontSize: '13px',
                                      fontWeight: 600,
                                      color: '#fff',
                                    }}>
                                      {actionLabel}
                                    </span>
                                    <span style={{
                                      fontSize: '11px',
                                      color: toolColor,
                                      fontWeight: 500,
                                      opacity: 0.8,
                                    }}>
                                      {TOOL_LABELS[event.tool] || event.tool}
                                    </span>
                                  </div>
                                  {metaStr && (
                                    <div style={{
                                      fontSize: '11px',
                                      color: '#666',
                                      marginTop: '2px',
                                      fontStyle: 'italic',
                                    }}>
                                      {metaStr}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* No data state */}
      {!loading && !journeyData && searched && !error && (
        <p className="no-data">No data found for this user.</p>
      )}
    </div>
  );
}

export default ReportsUserJourney;
